/*
  warranty-clover-checkout.js
  ------------------------------------------------------------------
  Same-page Clover checkout controller for product.warranty-product.

  Scope: only runs when [data-warranty-checkout] is present, which only
  the warranty template renders. Normal product pages are untouched.

  Design rules enforced here:
    - The backend is authoritative. The charge amount is always the
      amount_cents returned by session.php, never a DOM/Liquid price.
    - Fail closed. Any failure shows the unavailable panel; this file
      contains no path back to Shopify checkout, PayPal or Shop Pay.
    - Never double-charge. On timeout or RECONCILIATION_REQUIRED we poll
      status.php - we never re-POST pay.php.
    - Card data never reaches this file. Clover hosted iframes own the
      number / expiry / CVV / ZIP fields; we only receive a source token.
  ------------------------------------------------------------------ */

(function () {
  'use strict';

  var root = document.querySelector('[data-warranty-checkout]');
  if (!root) return;

  var BASE = (root.dataset.endpointBase || '').replace(/\/+$/, '');
  var PRODUCT_GID = root.dataset.productGid || '';
  var SHOP_PHONE = root.dataset.shopPhone || '';

  var SESSION_URL = BASE + '/session.php';
  var PAY_URL = BASE + '/pay.php';
  var STATUS_URL = BASE + '/status.php';

  var PAY_TIMEOUT_MS = 45000;
  var SESSION_TIMEOUT_MS = 20000;
  var POLL_INTERVAL_MS = 4000;
  var POLL_MAX_MS = 120000;

  var el = {
    boot: root.querySelector('[data-wc-boot]'),
    form: root.querySelector('[data-wc-form]'),
    alert: root.querySelector('[data-wc-alert]'),
    submit: root.querySelector('[data-wc-submit]'),
    submitText: root.querySelector('[data-wc-submit-text]'),
    total: root.querySelector('[data-wc-total]'),
    totalAmount: root.querySelector('[data-wc-total-amount]'),
    unavailable: root.querySelector('[data-wc-unavailable]'),
    unavailableTitle: root.querySelector('[data-wc-unavailable-title]'),
    unavailableCopy: root.querySelector('[data-wc-unavailable-copy]'),
    card: root.querySelector('[data-wc-card]'),
    details: root.querySelector('[data-wc-details]'),
    success: root.querySelector('[data-wc-success]'),
    receipt: root.querySelector('[data-wc-receipt]')
  };

  /* ---------------------------------------------------------------- */

  var state = {
    session: null,       // latest READY session payload
    variantGid: root.dataset.variantGid || '',
    requestSeq: 0,       // guards against out-of-order session responses
    clover: null,
    cloverCfg: null,
    elements: null,
    mounted: false,
    /* Explicit checkout phase. Never inferred from button text.
       collapsed -> open -> processing -> paid
       Only a click while phase === 'open' can reach pay.php. */
    phase: 'collapsed',
    sdkPromise: null,    // ensures the SDK is only ever loaded once
    paying: false,
    settled: false,      // terminal state reached: never pay again
    debounce: null
  };

  function show(node) { if (node) node.hidden = false; }
  function hide(node) { if (node) node.hidden = true; }

  function setAlert(message) {
    if (!el.alert) return;
    if (!message) { el.alert.textContent = ''; hide(el.alert); return; }
    el.alert.textContent = message;
    show(el.alert);
  }

  function setBoot(message) {
    if (!el.boot) return;
    if (!message) { hide(el.boot); return; }
    el.boot.textContent = message;
    show(el.boot);
  }

  /* Fail closed. No Shopify / PayPal / Shop Pay fallback exists here. */
  function failClosed(title, copy) {
    state.settled = true;
    hide(el.boot);
    hide(el.form);
    hide(el.success);
    if (title && el.unavailableTitle) el.unavailableTitle.textContent = title;
    if (copy && el.unavailableCopy) el.unavailableCopy.textContent = copy;
    show(el.unavailable);
  }

  function setSubmitEnabled(enabled) {
    if (!el.submit) return;
    el.submit.disabled = !enabled;
  }

  function setSubmitLabel(text) {
    if (el.submitText) el.submitText.textContent = text;
  }

  /* One place derives the button wording from the phase, so a variant
     change mid-flow can never leave the wrong label on the button. */
  function updateSubmitLabel() {
    var amount = money(state.session);
    if (state.phase === 'processing') { setSubmitLabel('Processing…'); return; }
    if (state.phase === 'open') {
      setSubmitLabel(amount ? 'Complete Purchase — ' + amount : 'Complete Purchase');
      return;
    }
    setSubmitLabel(amount ? 'Buy Online Now — ' + amount : 'Buy Online Now');
  }

  function money(session) {
    if (!session) return '';
    if (session.amount) return '$' + session.amount;
    if (typeof session.amount_cents === 'number') {
      return '$' + (session.amount_cents / 100).toFixed(2);
    }
    return '';
  }

  /* Safe user-facing message. Never surfaces raw gateway text, ids or
     stack traces - only the backend's own customer-facing `message`. */
  function safeMessage(payload, fallback) {
    if (payload && typeof payload.message === 'string' && payload.message.length < 300) {
      return payload.message;
    }
    return fallback;
  }

  function postJSON(url, body, timeoutMs) {
    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = null;
    var opts = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    };
    if (controller) opts.signal = controller.signal;

    var p = fetch(url, opts).then(function (res) {
      return res.json().catch(function () {
        throw new Error('bad_json');
      });
    });

    if (controller && timeoutMs) {
      timer = setTimeout(function () { controller.abort(); }, timeoutMs);
    }
    return p.finally(function () { if (timer) clearTimeout(timer); });
  }

  /* ---------------------------------------------------------------- *
   * Clover SDK - loaded at most once per page
   * ---------------------------------------------------------------- */

  function loadCloverSdk(url) {
    if (state.sdkPromise) return state.sdkPromise;
    state.sdkPromise = new Promise(function (resolve, reject) {
      if (window.Clover) { resolve(); return; }
      var existing = document.querySelector('script[data-clover-sdk]');
      if (existing) {
        existing.addEventListener('load', function () { resolve(); });
        existing.addEventListener('error', function () { reject(new Error('sdk')); });
        return;
      }
      var s = document.createElement('script');
      s.src = url || 'https://checkout.clover.com/sdk.js';
      s.async = true;
      s.setAttribute('data-clover-sdk', 'true');
      s.onload = function () { resolve(); };
      s.onerror = function () { reject(new Error('sdk')); };
      document.head.appendChild(s);
    });
    return state.sdkPromise;
  }

  /* Mount hosted fields once. Uses the same element names and call shape
     as the existing Poway Clover integration on the OTD purchase page. */
  function mountCloverFields(cfg) {
    if (state.mounted) return true;
    if (!window.Clover || !cfg || !cfg.public_token || !cfg.merchant_id) return false;

    /* These styles are applied INSIDE Clover's cross-origin iframes - our
       stylesheet cannot reach that content, so the styles API is the only
       way to control it.

       This is also the real fix for the field height. Clover's iframe
       sizes itself to its own content: the default <body> margin plus the
       input's natural line-height made that content taller than the host
       box, so locking only the outer iframe clipped it. Zeroing the body
       box and pinning the input to an explicit 46px line makes the
       content genuinely 46px, so the 48px host container fits it with
       room to spare instead of cropping it. */
    var CARD_FONT_PX = '18px';
    var CARD_LINE_PX = '46px';

    var styles = {
      body: {
        fontFamily: 'inherit',
        fontSize: CARD_FONT_PX,
        margin: '0',
        padding: '0'
      },
      input: {
        fontSize: CARD_FONT_PX,
        fontFamily: 'inherit',
        color: '#111827',
        height: CARD_LINE_PX,
        lineHeight: CARD_LINE_PX,
        margin: '0',
        padding: '0',
        border: '0',
        background: 'transparent'
      },
      'input::placeholder': {
        color: '#9ca3af',
        fontSize: CARD_FONT_PX
      }
    };

    state.clover = new window.Clover(cfg.public_token, { merchantId: cfg.merchant_id });
    state.elements = state.clover.elements();
    state.elements.create('CARD_NUMBER', styles).mount('#wc-cc-number');
    state.elements.create('CARD_DATE', styles).mount('#wc-cc-date');
    state.elements.create('CARD_CVV', styles).mount('#wc-cc-cvv');
    state.elements.create('CARD_POSTAL_CODE', styles).mount('#wc-cc-postal');
    state.mounted = true;
    return true;
  }

  /* ---------------------------------------------------------------- *
   * Session
   * ---------------------------------------------------------------- */

  function applySession(session) {
    state.session = session;
    var amount = money(session);
    if (el.totalAmount) el.totalAmount.textContent = amount;
    if (el.total) el.total.hidden = !amount;
    updateSubmitLabel();
  }

  /* The backend's own readiness verdict gates the payment button.
     `write_orders` is checked when present so that a backend which
     starts reporting it can switch this off without a theme change. */
  function sessionIsReady(payload) {
    if (!payload || payload.ok !== true) return false;
    if (payload.state && payload.state !== 'READY') return false;
    if (payload.write_orders === false || payload.write_orders === 'No') return false;
    if (payload.preflight && payload.preflight.write_orders === false) return false;
    return !!payload.session_token;
  }

  function requestSession(variantGid) {
    if (state.settled) return;
    if (!variantGid) {
      failClosed(
        'Online checkout temporarily unavailable.',
        'This plan option is not available to purchase online. Please contact Poway Powersports at ' + SHOP_PHONE + '.'
      );
      return;
    }

    var seq = ++state.requestSeq;

    /* The previous session is dropped the moment a new one is requested,
       so a stale session can never be the one that gets charged. */
    state.session = null;
    setSubmitEnabled(false);
    setAlert('');
    setBoot('Preparing secure checkout…');

    postJSON(
      SESSION_URL,
      { product_gid: PRODUCT_GID, variant_gid: variantGid },
      SESSION_TIMEOUT_MS
    )
      .then(function (payload) {
        /* Out-of-order response from an older variant click: discard. */
        if (seq !== state.requestSeq) return;

        if (!sessionIsReady(payload)) {
          failClosed(
            'Online checkout temporarily unavailable.',
            safeMessage(
              payload,
              'Please contact Poway Powersports at ' + SHOP_PHONE + ' to purchase this HondaCare plan.'
            )
          );
          return;
        }

        applySession(payload);

        var cfg = payload.clover || {};
        state.cloverCfg = cfg;

        /* The SDK is loaded up front so a broken SDK still fails closed
           before the customer can type anything, but the hosted fields
           are only MOUNTED when the card section is revealed - mounting
           into a hidden container gives Clover the wrong dimensions. */
        return loadCloverSdk(cfg.sdk_url)
          .then(function () {
            if (seq !== state.requestSeq) return;
            if (!window.Clover || !cfg.public_token || !cfg.merchant_id) {
              failClosed(
                'Online checkout temporarily unavailable.',
                'The secure payment form could not be loaded. Please contact Poway Powersports at ' + SHOP_PHONE + '.'
              );
              return;
            }
            setBoot('');
            show(el.form);
            setSubmitEnabled(true);
          })
          .catch(function () {
            failClosed(
              'Online checkout temporarily unavailable.',
              'The secure payment form could not be loaded. Please contact Poway Powersports at ' + SHOP_PHONE + '.'
            );
          });
      })
      .catch(function () {
        if (seq !== state.requestSeq) return;
        failClosed(
          'Online checkout temporarily unavailable.',
          'Please contact Poway Powersports at ' + SHOP_PHONE + ' to purchase this HondaCare plan.'
        );
      });
  }

  /* ---------------------------------------------------------------- *
   * Variant changes
   * ---------------------------------------------------------------- */

  function readSelectedVariantGid() {
    var node = document.querySelector('variant-selects [data-selected-variant]');
    if (!node) return '';
    try {
      var v = JSON.parse(node.textContent);
      return v && v.id ? 'gid://shopify/ProductVariant/' + v.id : '';
    } catch (e) {
      return '';
    }
  }

  function onVariantChanged(variantId) {
    if (state.settled) return;
    var gid = variantId ? 'gid://shopify/ProductVariant/' + variantId : readSelectedVariantGid();
    if (!gid || gid === state.variantGid) return;
    state.variantGid = gid;

    /* Debounced so rapid term/roadside clicking issues one request, and
       seq-guarded so only the newest response is ever applied. */
    if (state.debounce) clearTimeout(state.debounce);
    state.debounce = setTimeout(function () {
      requestSession(gid);
    }, 250);
  }

  /* ---------------------------------------------------------------- *
   * Payment
   * ---------------------------------------------------------------- */

  /* A VIN is 17 chars, letters+digits, and never contains I, O or Q
     (they are excluded to avoid confusion with 1 and 0). Frontend rule
     only - the backend remains authoritative. */
  var VIN_ALLOWED = /[^A-HJ-NPR-Z0-9]/g;
  var VIN_VALID = /^[A-HJ-NPR-Z0-9]{17}$/;

  function validateCustomer(data) {
    if (!data.first_name) return 'Please enter your first name.';
    if (!data.last_name) return 'Please enter your last name.';
    if (!data.email || data.email.indexOf('@') === -1) return 'Please enter a valid email address.';
    if (!data.phone) return 'Please enter a phone number.';
    if (!data.vin) return 'Please enter the vehicle VIN.';
    if (data.vin.length !== 17) return 'The VIN must be exactly 17 characters.';
    if (!VIN_VALID.test(data.vin)) {
      return 'Please enter a valid VIN. VINs use letters and numbers only, and never contain I, O or Q.';
    }
    return null;
  }

  /* Uppercase, strip disallowed characters and cap at 17 as the customer
     types, keeping the caret roughly where they left it. */
  function sanitizeVin(node) {
    if (!node) return;
    var before = node.value;
    var after = before.toUpperCase().replace(VIN_ALLOWED, '').slice(0, 17);
    if (after === before) return;
    var caret = node.selectionStart;
    var removed = before.length - after.length;
    node.value = after;
    if (typeof caret === 'number') {
      var pos = Math.max(0, caret - removed);
      try { node.setSelectionRange(pos, pos); } catch (e) { /* unsupported input type */ }
    }
  }

  function readCustomer() {
    var get = function (name) {
      var node = el.form.querySelector('[name="' + name + '"]');
      return node ? node.value.trim() : '';
    };
    return {
      first_name: get('first_name'),
      last_name: get('last_name'),
      email: get('email'),
      phone: get('phone'),
      vin: get('vin').toUpperCase()
    };
  }

  function renderReceipt(payload) {
    if (!el.receipt) return;
    el.receipt.textContent = '';
    var rows = [
      ['Plan', payload.plan],
      ['Total', payload.amount ? '$' + payload.amount : money(payload)],
      ['Confirmation', payload.order_name || payload.order_number || payload.confirmation],
      ['Confirmation sent to', payload.email]
    ];
    rows.forEach(function (row) {
      if (!row[1]) return; /* only render what the backend actually returned */
      var dt = document.createElement('dt');
      dt.className = 'warranty-checkout__receipt-label';
      dt.textContent = row[0];
      var dd = document.createElement('dd');
      dd.className = 'warranty-checkout__receipt-value';
      dd.textContent = row[1];
      el.receipt.appendChild(dt);
      el.receipt.appendChild(dd);
    });
  }

  function showSuccess(payload) {
    state.settled = true;
    state.paying = false;
    state.phase = 'paid';
    hide(el.boot);
    hide(el.form);
    hide(el.unavailable);
    renderReceipt(payload || {});
    show(el.success);
  }

  function showPending(message) {
    state.settled = true; /* no further pay attempts from this page */
    state.paying = false;
    setSubmitEnabled(false);
    setSubmitLabel('Payment submitted');
    setAlert(message);
  }

  /* Never re-POST pay.php. Only ever ask status.php what happened. */
  function pollStatus(sessionToken, startedAt) {
    var started = startedAt || Date.now();

    postJSON(STATUS_URL, { session_token: sessionToken }, SESSION_TIMEOUT_MS)
      .then(function (payload) {
        var st = payload && payload.state;

        if (payload && payload.ok === true && st === 'PAID') {
          showSuccess(payload);
          return;
        }

        if (st === 'FAILED' || st === 'DECLINED') {
          state.settled = false;
          state.paying = false;
          state.phase = 'open';
          setSubmitEnabled(true);
          updateSubmitLabel();
          setAlert(safeMessage(payload, 'That payment was declined. Please check your card details and try again.'));
          return;
        }

        if (Date.now() - started < POLL_MAX_MS) {
          setTimeout(function () { pollStatus(sessionToken, started); }, POLL_INTERVAL_MS);
          return;
        }

        showPending(
          'We received your payment request but cannot yet confirm its final status. Do not submit another payment. Please contact Poway Powersports at ' +
            SHOP_PHONE + '.'
        );
      })
      .catch(function () {
        if (Date.now() - started < POLL_MAX_MS) {
          setTimeout(function () { pollStatus(sessionToken, started); }, POLL_INTERVAL_MS);
          return;
        }
        showPending(
          'We received your payment request but cannot yet confirm its final status. Do not submit another payment. Please contact Poway Powersports at ' +
            SHOP_PHONE + '.'
        );
      });
  }

  function handlePayResponse(payload, sessionToken) {
    var st = payload && payload.state;

    if (payload && payload.ok === true && st === 'PAID') {
      showSuccess(payload);
      return;
    }

    if (st === 'RECONCILIATION_REQUIRED') {
      setAlert('Checking payment status…');
      setSubmitEnabled(false);
      pollStatus(sessionToken);
      return;
    }

    /* Ordinary decline / failure: stay on the page and allow a retry only
       when the backend says the condition is retryable. */
    state.paying = false;
    state.phase = 'open';
    var message = safeMessage(payload, 'We could not complete that payment. Please check your card details and try again.');

    if (payload && payload.retryable === false) {
      setAlert(message);
      setSubmitEnabled(false);
      setSubmitLabel('Payment unavailable');
      return;
    }

    setAlert(message);
    setSubmitEnabled(true);
    updateSubmitLabel();
  }

  /* collapsed -> open. Opens the checkout and mounts the Clover fields.
     Deliberately performs NO validation and NO network call: the first
     click is only ever "open the form". */
  function openCheckout() {
    if (!mountCloverFields(state.cloverCfg)) {
      setAlert('The secure payment form could not be loaded. Please refresh the page and try again.');
      return false;
    }
    state.phase = 'open';
    show(el.details);
    if (el.details && el.details.classList) el.details.classList.add('is-open');
    updateSubmitLabel();

    var target = el.details || el.card;
    if (target && typeof target.scrollIntoView === 'function') {
      try {
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } catch (e) {
        target.scrollIntoView();
      }
    }
    return true;
  }

  function submit(event) {
    event.preventDefault();

    if (state.paying || state.settled) return;      /* double-click guard */
    if (state.phase === 'processing' || state.phase === 'paid') return;
    if (!state.session || !state.session.session_token) {
      setAlert('Checkout is still preparing. Please try again in a moment.');
      return;
    }

    /* ---- FIRST CLICK (collapsed): open only. No validation, no
       tokenisation, no pay.php. ---- */
    if (state.phase !== 'open') {
      setAlert('');
      openCheckout();
      return;
    }

    /* ---- Clicks while OPEN: the real payment. Purchaser details are
       validated here, now that the fields are actually on screen. ---- */
    var customer = readCustomer();
    var invalid = validateCustomer(customer);
    if (invalid) { setAlert(invalid); return; }

    if (!state.mounted || !state.clover) {
      setAlert('The secure payment form is not ready yet. Please try again in a moment.');
      return;
    }

    var sessionToken = state.session.session_token;

    state.paying = true;
    state.phase = 'processing';
    setSubmitEnabled(false);
    updateSubmitLabel();
    setAlert('');

    state.clover
      .createToken()
      .then(function (result) {
        if (!result || result.errors) {
          state.paying = false;
          state.phase = 'open';
          setSubmitEnabled(true);
          updateSubmitLabel();
          setAlert('Please check your card details and try again.');
          return;
        }
        if (!result.token) {
          state.paying = false;
          state.phase = 'open';
          setSubmitEnabled(true);
          updateSubmitLabel();
          setAlert('Please check your card details and try again.');
          return;
        }

        return postJSON(
          PAY_URL,
          {
            session_token: sessionToken,
            source_token: result.token,
            first_name: customer.first_name,
            last_name: customer.last_name,
            email: customer.email,
            phone: customer.phone,
            vin: customer.vin
          },
          PAY_TIMEOUT_MS
        )
          .then(function (payload) {
            handlePayResponse(payload, sessionToken);
          })
          .catch(function () {
            /* Timed out or the connection dropped AFTER submitting. The
               charge may or may not have gone through, so we ask - we
               never resubmit. */
            setAlert('Checking payment status…');
            setSubmitEnabled(false);
            pollStatus(sessionToken);
          });
      })
      .catch(function () {
        state.paying = false;
        setSubmitEnabled(true);
        updateSubmitLabel();
        setAlert('Please check your card details and try again.');
      });
  }

  /* ---------------------------------------------------------------- *
   * Boot
   * ---------------------------------------------------------------- */

  function init() {
    if (!BASE) {
      failClosed(
        'Online checkout temporarily unavailable.',
        'Please contact Poway Powersports at ' + SHOP_PHONE + ' to purchase this HondaCare plan.'
      );
      return;
    }

    if (el.form) el.form.addEventListener('submit', submit);

    var vinInput = root.querySelector('[name="vin"]');
    if (vinInput) {
      vinInput.addEventListener('input', function () { sanitizeVin(vinInput); });
      vinInput.addEventListener('blur', function () { sanitizeVin(vinInput); });
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        var variant = event && event.data && event.data.variant;
        onVariantChanged(variant && variant.id);
      });
    }

    var initialGid = state.variantGid || readSelectedVariantGid();
    state.variantGid = initialGid;
    requestSession(initialGid);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
